import { AvailabilityTimeFrame, type AvailabilityRule } from '@kouto/types';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { format, parseISO } from 'date-fns';
import useIsMobile from 'hooks/useIsMobile';
import {
  deriveMaxUnits,
  deriveRangeFromUnits,
  deriveUnitsFromRange,
  isStayDateSelectable,
  isStayRangeBookable,
} from 'utils/continuousSelection';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface StayRangePickerProps {
  /** Dates on which at least one resource still has a bookable span, "YYYY-MM-DD". */
  availableDates: readonly string[];
  /** The start date in force, "YYYY-MM-DD". */
  date: string;
  isLoadingAvailableDates: boolean;
  /** The calendar has moved to another month; the parent widens what it knows is available. */
  onMonthChange: (month: Date) => void;
  /** A start and end have both been picked. */
  onRangeChange: (date: string, units: number) => void;
  rule: AvailabilityRule;
  timeFrame: AvailabilityTimeFrame;
  /** How many days or nights are currently held, when a stay is picked. */
  units?: number;
}

const toDateString = (date: Date): string => format(date, 'yyyy-MM-dd');
const fromDateString = (date: string): Date => parseISO(`${date}T12:00:00`);

/**
 * The start and end dates of a day-based stay, picked on one two-month calendar — one month on a phone,
 * where a second would run past the edge of the page.
 *
 * Two cells read as one field: the first click is the start, the second the end, and a click
 * before the start swaps them. Dates every resource is booked on are greyed out, as are dates a stay
 * from the start could not reach — past the rule's longest stay, or past the first booked-out date.
 */
const StayRangePicker = ({
  availableDates,
  date,
  isLoadingAvailableDates,
  onMonthChange,
  onRangeChange,
  rule,
  timeFrame,
  units,
}: StayRangePickerProps) => {
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const isNightly = timeFrame === AvailabilityTimeFrame.NIGHTLY;

  const committed = useMemo<DateRange | undefined>(() => {
    if (!units) {
      return undefined;
    }
    const { from, to } = deriveRangeFromUnits({ date, timeFrame, units });
    return { from: fromDateString(from), to: fromDateString(to) };
  }, [date, timeFrame, units]);

  // The calendar's own draft while it is open, so a half-picked stay never reaches the URL.
  const [draft, setDraft] = useState<DateRange | undefined>(committed);
  useEffect(() => {
    if (!open) {
      setDraft(committed);
    }
  }, [committed, open]);

  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);
  const maxUnits = useMemo(() => deriveMaxUnits(rule), [rule]);
  const today = toDateString(new Date());

  const isDisabled = useCallback(
    (day: Date): boolean =>
      isLoadingAvailableDates ||
      !isStayDateSelectable({
        availableDates: availableSet,
        date: toDateString(day),
        from: draft?.from && !draft.to ? toDateString(draft.from) : null,
        maxUnits,
        timeFrame,
        today,
      }),
    [availableSet, draft, isLoadingAvailableDates, maxUnits, timeFrame, today],
  );

  const handleSelect = (_next: DateRange | undefined, clicked: Date) => {
    if (!draft?.from || draft.to) {
      setDraft({ from: clicked, to: undefined });
      return;
    }
    const [from, to] =
      clicked < draft.from ? [clicked, draft.from] : [draft.from, clicked];
    // A click before the start swaps the two; the swapped stay is held to the same limits, else the
    // click starts a new stay from that date instead.
    if (
      clicked < draft.from &&
      !isStayRangeBookable({
        availableDates: availableSet,
        from: toDateString(from),
        maxUnits,
        timeFrame,
        to: toDateString(to),
      })
    ) {
      setDraft({ from: clicked, to: undefined });
      return;
    }
    const picked = deriveUnitsFromRange({
      from: toDateString(from),
      timeFrame,
      to: toDateString(to),
    });
    if (!picked) {
      // The same day twice: a nightly stay needs a later end date, a daily one is a single day.
      if (isNightly) {
        setDraft({ from: clicked, to: undefined });
        return;
      }
      setDraft({ from, to });
      setOpen(false);
      onRangeChange(toDateString(from), 1);
      return;
    }
    setDraft({ from, to });
    setOpen(false);
    onRangeChange(toDateString(from), picked);
  };

  const labelFrom = translate('startDate');
  const labelTo = translate('endDate');
  const shown = open ? draft : committed;
  const describe = (day: Date | undefined): string =>
    day ? format(day, 'MMM d', { locale }) : translate('addDate');

  return (
    <DayPickerPopover
      asChild
      isLoading={isLoadingAvailableDates}
      open={open}
      onOpenChange={setOpen}
      mode="range"
      numberOfMonths={isMobile ? 1 : 2}
      defaultMonth={committed?.from ?? fromDateString(date)}
      selected={shown}
      onSelect={handleSelect}
      onMonthChange={onMonthChange}
      disabled={isDisabled}
      className="stay-range-calendar"
      footer={null}
      sideOffset={10}
      side="bottom"
      align="start"
      trigger={
        <Field
          type="button"
          className="stay-range-picker"
          data-testid="stay-range-picker"
          aria-label={translate('selectDates')}
          aria-expanded={open}
        >
          <Cell
            className="stay-range-picker__from"
            data-testid="stay-range-picker-from"
          >
            <CellLabel className="stay-range-picker__label">
              {labelFrom}
            </CellLabel>
            <CellValue data-testid="stay-from">
              {describe(shown?.from)}
            </CellValue>
          </Cell>
          <Cell
            className="stay-range-picker__to"
            data-testid="stay-range-picker-to"
          >
            <CellLabel className="stay-range-picker__label">
              {labelTo}
            </CellLabel>
            <CellValue data-testid="stay-to">{describe(shown?.to)}</CellValue>
          </Cell>
        </Field>
      }
    />
  );
};

const Field = styled.button`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  padding: 0;
  background: transparent;
  border: 0.5px solid var(--way-colors-borderColor);
  border-radius: var(--way-design-borderRadiusDefault);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  color: var(--way-colors-contrastColorShades-100);

  &:focus-visible {
    outline: 2px solid var(--way-colors-contrastColorShades-80);
    outline-offset: 2px;
  }
`;

const Cell = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;

  & + & {
    border-left: 0.5px solid var(--way-colors-borderColor);
  }
`;

const CellLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-70);
`;

const CellValue = styled.span`
  font-size: 16px;
  line-height: 20px;
`;

export default StayRangePicker;
